/**
 * Blocage progressif du pave NIP (plan, section 1.7), garde dans `meta`
 * (cle pin_lockout) : il survit a un rechargement ou a un redemarrage.
 *
 *   - 5 erreurs de suite : 1 minute de blocage ;
 *   - 10 erreurs de suite : 15 minutes ; a la fin de ces 15 minutes, le
 *     compteur repart de zero (comme le Punch, PunchOfflineDB.validatePinOffline,
 *     qui n'a que le palier 10 erreurs = 15 minutes) ;
 *   - une connexion reussie remet le compteur a zero.
 *
 * Pendant un blocage, aucun NIP n'est verifie (ni en ligne ni hors ligne) et
 * l'essai ne compte pas. Une horloge remise en arriere ne raccourcit pas le
 * blocage : un debut de blocage « dans le futur » le prolonge de sa duree
 * entiere a partir de maintenant.
 *
 * Forme : { failures, locked_at, locked_until } (millisecondes epoch, ou null).
 */
import { requestToPromise, transactionDone } from './idb';
import { META_KEYS, STORES } from './schema';

export const FIRST_LOCK = Object.freeze({ failures: 5, durationMs: 60_000 });
export const SECOND_LOCK = Object.freeze({ failures: 10, durationMs: 15 * 60_000 });

const EMPTY = Object.freeze({ failures: 0, locked_at: null, locked_until: null });

function clean(record) {
    if (!record || typeof record !== 'object') return { ...EMPTY };
    return {
        failures: Number.isSafeInteger(record.failures) && record.failures > 0 ? record.failures : 0,
        locked_at: Number.isFinite(record.locked_at) ? record.locked_at : null,
        locked_until: Number.isFinite(record.locked_until) ? record.locked_until : null,
    };
}

/**
 * Etat du blocage a l'instant `nowMs` (fonction pure).
 * @returns {{ locked: boolean, until: number|null, remainingMs: number, failures: number, expired: boolean }}
 *   expired : un blocage vient de finir (le compteur doit etre remis a zero
 *   s'il s'agissait du palier de 15 minutes).
 */
export function evaluateLockout(record, nowMs) {
    const r = clean(record);
    if (r.locked_until === null || r.locked_at === null) {
        return { locked: false, until: null, remainingMs: 0, failures: r.failures, expired: false };
    }
    const duration = Math.max(0, r.locked_until - r.locked_at);
    if (nowMs < r.locked_at) {
        // Horloge reculee : le blocage repart pour toute sa duree.
        return { locked: true, until: nowMs + duration, remainingMs: duration, failures: r.failures, expired: false };
    }
    if (nowMs < r.locked_until) {
        return { locked: true, until: r.locked_until, remainingMs: r.locked_until - nowMs, failures: r.failures, expired: false };
    }
    return { locked: false, until: null, remainingMs: 0, failures: r.failures, expired: true };
}

/** Nouvel etat apres une erreur a `nowMs` (fonction pure). */
export function nextAfterFailure(record, nowMs) {
    let r = clean(record);
    const state = evaluateLockout(r, nowMs);
    if (state.locked) return r; // un essai pendant le blocage ne compte pas
    if (state.expired) {
        r = r.failures >= SECOND_LOCK.failures ? { ...EMPTY } : { ...r, locked_at: null, locked_until: null };
    }
    const failures = r.failures + 1;
    if (failures >= SECOND_LOCK.failures) {
        return { failures, locked_at: nowMs, locked_until: nowMs + SECOND_LOCK.durationMs };
    }
    if (failures === FIRST_LOCK.failures) {
        return { failures, locked_at: nowMs, locked_until: nowMs + FIRST_LOCK.durationMs };
    }
    return { failures, locked_at: null, locked_until: null };
}

/** Etat courant, lu dans IndexedDB. */
export async function getLockout(db, nowMs = Date.now()) {
    const tx = db.transaction(STORES.META, 'readonly');
    const record = await requestToPromise(tx.objectStore(STORES.META).get(META_KEYS.PIN_LOCKOUT));
    return evaluateLockout(record ? record.value : null, nowMs);
}

/**
 * Compte une erreur, dans une transaction (lecture + ecriture) : deux erreurs
 * simultanees comptent pour deux.
 * @returns {Promise<ReturnType<typeof evaluateLockout>>} l'etat apres l'erreur
 */
export async function recordPinFailure(db, nowMs = Date.now()) {
    const tx = db.transaction(STORES.META, 'readwrite');
    const store = tx.objectStore(STORES.META);
    const done = transactionDone(tx);
    const record = await requestToPromise(store.get(META_KEYS.PIN_LOCKOUT));
    const next = nextAfterFailure(record ? record.value : null, nowMs);
    store.put({ key: META_KEYS.PIN_LOCKOUT, value: next });
    await done;
    return evaluateLockout(next, nowMs);
}

/** Connexion reussie : compteur a zero. */
export async function resetPinFailures(db) {
    const tx = db.transaction(STORES.META, 'readwrite');
    const done = transactionDone(tx);
    tx.objectStore(STORES.META).put({ key: META_KEYS.PIN_LOCKOUT, value: { ...EMPTY } });
    await done;
}

/** « Réessayez dans 1 minute » / « Réessayez dans 15 minutes ». */
export function lockoutMessage(remainingMs) {
    const minutes = Math.max(1, Math.ceil(remainingMs / 60_000));
    return `Réessayez dans ${minutes} minute${minutes > 1 ? 's' : ''}`;
}
